if (!customElements.get('slider-component')) {
  customElements.define('slider-component', class SliderComponent extends HTMLElement {
      constructor() {
        super();
        this.slider = this.querySelector('[id^="Slider-"]');
        this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
        this.enableSliderLooping = false;
        this.currentPageElement = this.querySelector('.slider-counter--current');
        this.pageTotalElement = this.querySelector('.slider-counter--total');
        this.prevButton = this.querySelector('button[name="previous"]');
        this.nextButton = this.querySelector('button[name="next"]');

        if (!this.slider || !this.nextButton) return;

        this.initPages();
        // ResizeObserver is missing on iOS <13.1 / older Android WebViews
        // (FB/IG in-app traffic) — unguarded use threw a ReferenceError that
        // killed the whole component. Fall back to a window resize listener.
        if (typeof ResizeObserver !== 'undefined') {
          // rAF defers the layout work to the next frame — calling initPages()
          // synchronously inside the callback re-triggers the observer and
          // fires "ResizeObserver loop completed with undelivered notifications"
          let rafId = null;
          const resizeObserver = new ResizeObserver(entries => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => this.initPages());
          });
          resizeObserver.observe(this.slider);
        } else {
          window.addEventListener('resize', () => this.initPages());
        }

        this.slider.addEventListener('scroll', this.update.bind(this));
        this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
        this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
      }

      initPages() {
        this.sliderItemsToShow = Array.from(this.sliderItems).filter(element => element.clientWidth > 0);
        if (this.sliderItemsToShow.length < 2) return;
        // Negative under RTL (slides run right to left); the step is a distance.
        this.sliderItemOffset = Math.abs(this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft);
        this.slidesPerPage = Math.floor((this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset);
        // When all items fit in the viewport the raw formula goes negative ("10 / -2")
        this.totalPages = Math.max(1, this.sliderItemsToShow.length - this.slidesPerPage + 1);
        this.update();
      }

      resetPages() {
        this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
        this.initPages();
      }

      update() {
        const previousPage = this.currentPage;
        // RTL scrollLeft runs negative, which pinned /ar on page 1 now that the phone gallery swipes.
        this.currentPage = Math.min(Math.max(1, Math.round(Math.abs(this.slider.scrollLeft) / this.sliderItemOffset) + 1), this.totalPages || 1);

        if (this.currentPageElement && this.pageTotalElement) {
          this.currentPageElement.textContent = this.currentPage;
          this.pageTotalElement.textContent = this.totalPages;
        }

        if (this.currentPage != previousPage) {
          this.dispatchEvent(new CustomEvent('slideChanged', { detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1]
          }}));
        }

        if (this.enableSliderLooping) return;

        if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
          this.prevButton.setAttribute('disabled', 'disabled');
        } else {
          this.prevButton.removeAttribute('disabled');
        }

        if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
          this.nextButton.setAttribute('disabled', 'disabled');
        } else {
          this.nextButton.removeAttribute('disabled');
        }
      }

      // On-screen boxes rather than offsetLeft vs scrollLeft, which only holds left to right.
      isSlideVisible(element, offset = 0) {
        const box = this.slider.getBoundingClientRect();
        const item = element.getBoundingClientRect();
        return Math.round(item.left) >= Math.round(box.left) && Math.round(item.right) <= Math.round(box.right) - offset;
      }

      onButtonClick(event) {
        event.preventDefault();
        const step = event.currentTarget.dataset.step || 1;
        // sliderItemOffset is a distance; under RTL "next" means a more negative scrollLeft.
        const distance = step * this.sliderItemOffset * (getComputedStyle(this.slider).direction === 'rtl' ? -1 : 1);
        this.slideScrollPosition = event.currentTarget.name === 'next' ? this.slider.scrollLeft + distance : this.slider.scrollLeft - distance;
        this.slider.scrollTo({
          left: this.slideScrollPosition
        });
      }

  });
}
